import { computed, onUnmounted, readonly, ref } from 'vue';
import { ProductAddOnModel, ProductClientService, PurchaseOptionType } from '@/types/productServerApi';
import utilities from '@/shared/utilities.service';
import { useRouteQuery } from '@/core/browser/route-query.composable';
import { QueryKeys } from '@/shared/config/route-query.config';
import { ProductAddOnTileModel } from '@/types/B2BcontentServerContract';
import { IBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import basketTrackingService from '@/shared/tracking/basket-tracking.service';

type LoadingState = 'loading' | 'loaded' | 'idle' | 'revalidating';

interface ProductAddOnWithSelected extends ProductAddOnModel {
    selected: boolean;
    basketState: 'adding' | 'added' | 'idle';
}

const addOns = ref<ProductAddOnWithSelected[]>([] as ProductAddOnWithSelected[]);
const selectedAddOns = computed(() => addOns.value.filter((addOn) => addOn.selected));
const hasSelectedAddOns = computed(() => selectedAddOns.value.length > 0);
const loadingState = ref<LoadingState>('idle');
const labels = ref<IBlockLabels>({});
const isRegistered = ref<boolean>(false);
const addedAddOnsLabel = computed(() => hasSelectedAddOns.value && labels.value.multipleAddedToBasket);

export default function useProductAddOns() {
    const isAddOnsEnabled = computed<boolean>(() => !editGuid.value && isRegistered.value);
    const editGuid = useRouteQuery(QueryKeys.Edit);
    const timeouts = ref<number[]>([]);

    const fetchAddOns = async(id: string): Promise<ProductAddOnModel[]> => {
        if (addOns.value.length > 0) {
            loadingState.value = 'revalidating';
        } else {
            loadingState.value = 'loading';
        }
        const response = await ProductClientService.getProductAddOns(id)
            .then(utilities.unwrapModel);
        addOns.value = response?.map(addOn => {
            return {
                ...addOn,
                selected: false,
                basketState: 'idle',
            };
        }) ?? [] as ProductAddOnWithSelected[];
        loadingState.value = 'loaded';

        return addOns.value;
    };

    const maybeFetchAddOns = async(id: string) => {
        if (isAddOnsEnabled.value && loadingState.value !== 'loading') {
            await fetchAddOns(id);
        }
    };

    const clearAddOns = () => {
        addOns.value.length = 0;
    };

    onUnmounted(() => {
        timeouts.value.forEach((timeout) => clearTimeout(timeout));
    });

    const forceAddingState = () => {
        for (const addOn of addOns.value) {
            if (addOn.selected) {
                addOn.basketState = 'adding';
            }
        }
    };

    const getPurchaseTypeToUse = (addOn: ProductAddOnModel, purchaseType?: PurchaseOptionType) => {
        if (purchaseType) {
            const addOnHasSamePurchaseTypeAsMainProduct = addOn.purchaseOptions.find((option) => option.type === purchaseType);
            return addOnHasSamePurchaseTypeAsMainProduct ? purchaseType : addOn.purchaseOptions[0].type;
        } else {
            return addOn.purchaseOptions[0].type;
        }
    };

    const addAddOnsToBasketSynchronous = async(addToBasketFn: (id: string, purchaseType: PurchaseOptionType, quantity?: number) => Promise<void>, purchaseType?: PurchaseOptionType) => {
        if (selectedAddOns.value.length === 0) {
            return;
        }
        basketTrackingService.addOns(selectedAddOns.value, purchaseType);
        for (const addOn of selectedAddOns.value) {
            const purchaseTypeToUse = getPurchaseTypeToUse(addOn, purchaseType);
            try {
                addOn.basketState = 'adding';
                await addToBasketFn(addOn.sku, purchaseTypeToUse, addOn.recommendedQuantity);
                addOn.basketState = 'added';
            } catch (e) {
                addOn.basketState = 'idle';
                throw e;
            } finally {
                const timeToShowAddedState = 2000;
                timeouts.value.push(
                    setTimeout(() => {
                        addOn.basketState = 'idle';
                        addOn.selected = false;
                    }, timeToShowAddedState));
            }
        }
    };

    const addAddOnsToBasket = async(addToBasketFn: (id: string, purchaseType: PurchaseOptionType, quantity?: number) => Promise<void>, purchaseType?: PurchaseOptionType) => {
        if (selectedAddOns.value.length === 0) {
            return;
        }
        basketTrackingService.addOns(selectedAddOns.value, purchaseType);
        const promises = selectedAddOns.value.map(async(addOn) => {
            const purchaseTypeToUse = getPurchaseTypeToUse(addOn, purchaseType);
            return addToBasketFn(addOn.sku, purchaseTypeToUse, addOn.recommendedQuantity).then(() => {
                addOn.basketState = 'idle';
                addOn.selected = false;
            });
        });

        return Promise.all(promises);
    };

    const register = (content: ProductAddOnTileModel) => {
        labels.value = content.dictionaryLabels as any;
        isRegistered.value = true;
    };

    const unregister = () => {
        labels.value = {};
        isRegistered.value = false;
    };

    return {
        addOns,
        isAddOnsEnabled,
        clearAddOns,
        loadingState,
        addAddOnsToBasketSynchronous,
        addAddOnsToBasket,
        forceAddingState,
        hasSelectedAddOns,
        register,
        unregister,
        labels,
        maybeFetchAddOns,
        isRegistered: readonly(isRegistered),
        addedAddOnsLabel,
    };
}
